import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { DialogCloseEvent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartUpsells extends Component {
  /** @type {Element | null} */
  #drawer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#update);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('scroll', this.#update, { capture: true, passive: true });
    window.addEventListener('resize', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#update);
    this.#drawer = null;
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('scroll', this.#update, { capture: true });
    window.removeEventListener('resize', this.#update);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  get #track() {
    return this.querySelector('[data-cart-upsells-track]');
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-cart-upsells-prev]')) this.#scroll(-1);
    else if (target.closest('[data-cart-upsells-next]')) this.#scroll(1);
  };

  #scroll(direction) {
    const track = this.#track;
    const card = track?.querySelector('.cart-upsell');
    if (!track || !card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;
    if (!step) return;
    const from = track.scrollLeft;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const target = Math.max(0, Math.min(Math.round(from / step + direction) * step, maxScroll));
    if (Math.abs(target - from) < 1) return;
    track.scrollTo({ left: target, behavior: 'smooth' });
    setTimeout(() => {
      if (Math.abs(track.scrollLeft - from) < 1) track.scrollTo({ left: target });
      this.#update();
    }, 250);
    setTimeout(this.#update, 800);
  }

  #update = () => {
    const track = this.#track;
    if (!track) return;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    this.querySelectorAll('[data-cart-upsells-prev]').forEach((button) => button.toggleAttribute('disabled', atStart));
    this.querySelectorAll('[data-cart-upsells-next]').forEach((button) => button.toggleAttribute('disabled', atEnd));
    const card = track.querySelector('.cart-upsell');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : 0;
    const activeIndex = step ? Math.round(track.scrollLeft / step) : 0;
    this.querySelectorAll('[data-cart-upsells-dot]').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  };
}

if (!customElements.get('cart-upsells')) customElements.define('cart-upsells', CartUpsells);

class CartUpsellQuickAdd extends QuickAddComponent {
  constructor() {
    super();
    const openModal = this.handleClick;
    this.handleClick = async (event) => {
      this.#releaseDrawerFocusTrap();
      await openModal(event);
    };
  }

  get productPageUrl() {
    return this.dataset.productUrl || '';
  }

  #releaseDrawerFocusTrap() {
    const drawer = this.closest('theme-drawer');
    const panel = drawer?.querySelector('dialog');
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!drawer || !panel || !dialogComponent) return;
    if (!panel.open || panel.matches(':modal')) return;
    removeTrapFocus();
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => {
      if (drawer.hasAttribute('open') && panel.open && !panel.matches(':modal')) trapFocus(panel);
    }, { once: true });
  }
}

if (!customElements.get('cart-upsell-quick-add')) customElements.define('cart-upsell-quick-add', CartUpsellQuickAdd);
